import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'

interface GrowthComparisonChartProps {
  data: Record<string, number>
  height?: number
  t: TFunction
}

export interface GrowthComparisonChartRef {
  exportChart: () => string | null
}

export const GrowthComparisonChart = forwardRef<
  GrowthComparisonChartRef,
  GrowthComparisonChartProps
>(({ data, height = 350, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Prepare data
    const periods = Object.keys(data)
    const growthValues = Object.values(data)

    // Get colors based on growth values
    const getBarColor = (value: number) => {
      if (value > 10) return '#22c55e' // High growth - green
      if (value > 2) return '#3b82f6' // Good growth - blue
      if (value > 0) return '#10b981' // Positive growth - emerald
      if (value > -2) return '#f59e0b' // Slight decline - amber
      if (value > -10) return '#ef4444' // Decline - red
      return '#dc2626' // Significant decline - dark red
    }

    const barData = growthValues.map((value, index) => ({
      value: value,
      name: periods[index],
      itemStyle: {
        color: getBarColor(value),
        borderRadius: [4, 4, 0, 0],
      },
    }))

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          type: 'shadow',
        },
        formatter: function (params: any) {
          const item = params[0]
          const value = item.value
          const period = item.name

          const growthDescription =
            value > 10
              ? t('charts.growthComparison.descriptions.exceptional')
              : value > 5
                ? t('charts.growthComparison.descriptions.strong')
                : value > 2
                  ? t('charts.growthComparison.descriptions.good')
                  : value > 0
                    ? t('charts.growthComparison.descriptions.modest')
                    : value > -2
                      ? t('charts.growthComparison.descriptions.slightDecline')
                      : value > -5
                        ? t(
                            'charts.growthComparison.descriptions.moderateDecline'
                          )
                        : t(
                            'charts.growthComparison.descriptions.significantDecline'
                          )

          const marketImplication =
            value > 5
              ? t('charts.growthComparison.implications.excellent')
              : value > 2
                ? t('charts.growthComparison.implications.favorable')
                : value > 0
                  ? t('charts.growthComparison.implications.stable')
                  : value > -2
                    ? t('charts.growthComparison.implications.cautious')
                    : t('charts.growthComparison.implications.careful')

          return `
            <div style="text-align: left;">
              <strong>${period} ${t('charts.growthComparison.tooltips.annualized')}</strong><br/>
              <span style="color: ${getBarColor(value)};">● ${value >= 0 ? '+' : ''}${value.toFixed(2)}% ${t('charts.growthComparison.tooltips.perYear')}</span><br/>
              <span style="color: #6b7280;">${growthDescription}</span><br/>
              <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                <strong>${t('charts.growthComparison.tooltips.marketImpact')}:</strong><br/>
                <span style="color: #4b5563;">${marketImplication}</span>
              </div>
            </div>
          `
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: 50,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: periods,
        axisLabel: {
          fontSize: 11,
          margin: 8,
          color: '#1f2937',
        },
        axisLine: {
          lineStyle: {
            color: '#e5e7eb',
          },
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        name: t('charts.growthComparison.axis.annualGrowthRate'),
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: {
          fontSize: 11,
          color: '#6b7280',
        },
        axisLabel: {
          fontSize: 11,
          formatter: function (value: number) {
            return `${value >= 0 ? '+' : ''}${value}%`
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: '#f3f4f6',
            type: 'dashed',
          },
        },
      },
      series: [
        {
          name: t('charts.series.growthRate'),
          type: 'bar',
          data: barData,
          barWidth: '60%',
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          label: {
            show: true,
            position: 'top',
            formatter: function (params: any) {
              const value = params.value
              return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%/${t('charts.growthComparison.tooltips.perYear')}`
            },
            fontSize: 11,
            fontWeight: 'bold',
            color: '#374151',
          },
        },
      ],
    }

    // Add reference line at 0%
    if (Math.min(...growthValues) < 0 && Array.isArray(option.series)) {
      option.series.push({
        name: t('charts.series.zeroGrowth'),
        type: 'line',
        data: new Array(periods.length).fill(0),
        lineStyle: {
          color: '#6b7280',
          type: 'solid',
          width: 1,
        },
        symbol: 'none',
        silent: true,
        z: 1,
      })
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height])

  return (
    <div
      ref={chartRef}
      style={{
        width: '100%',
        height: `${height}px`,
        minHeight: '300px',
      }}
    />
  )
})

GrowthComparisonChart.displayName = 'GrowthComparisonChart'
